import * as Dialog from '@radix-ui/react-dialog'
import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { Sparkles, ChevronLeft, ChevronRight, X } from 'lucide-react'
import { useWhatsNew } from '../hooks/useWhatsNew'
import { updateWhatsNewShowOnLogin } from '../lib/api'
import { cn } from '../lib/utils'
import { ImageLightbox } from './ui/ImageLightbox'
import type { ReleaseNote } from '../lib/api'

const categoryConfig: Record<string, { label: string; color: string; bgColor: string }> = {
  feature: { label: 'New Feature', color: 'text-blue-400', bgColor: 'bg-blue-500/15' },
  improvement: { label: 'Improvement', color: 'text-green-400', bgColor: 'bg-green-500/15' },
  bugfix: { label: 'Bug Fix', color: 'text-amber-400', bgColor: 'bg-amber-500/15' },
  major: { label: 'Major Release', color: 'text-purple-400', bgColor: 'bg-purple-500/15' },
}

function CategoryPills({ categories }: { categories: string[] }) {
  const counts: Record<string, number> = {}
  for (const cat of categories) {
    counts[cat] = (counts[cat] || 0) + 1
  }
  const order = ['feature', 'improvement', 'bugfix', 'major']
  const sorted = order.filter((c) => counts[c])

  return (
    <div className="flex flex-wrap gap-1.5">
      {sorted.map((cat) => {
        const config = categoryConfig[cat] || { label: cat, color: 'text-zinc-400', bgColor: 'bg-zinc-500/15' }
        return (
          <span key={cat} className={cn('px-2 py-0.5 text-[10px] font-medium rounded-full', config.bgColor, config.color)}>
            {counts[cat]} {config.label}{counts[cat] > 1 ? 's' : ''}
          </span>
        )
      })}
    </div>
  )
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

function Slide({
  release,
  onViewChangelog,
  onImageClick,
}: {
  release: ReleaseNote
  onViewChangelog: (e: React.MouseEvent) => void
  onImageClick: (src: string, alt: string) => void
}) {
  const changeCats = release.changes.map((c) => c.category)

  return (
    <div className="min-w-full px-1">
      <div className="space-y-4">
        {/* Version + Title + Date */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-sm font-mono text-muted-foreground">{release.version}</span>
            <span className="text-sm text-muted-foreground">{formatDate(release.date)}</span>
          </div>
          <h3 className="text-xl font-bold">{release.title}</h3>
          <CategoryPills categories={changeCats} />
        </div>

        {/* Summary + view full notes */}
        <div className="space-y-1">
          <p className="text-sm text-muted-foreground line-clamp-3">{release.summary}</p>
          <Link
            to={`/changelog/${release.version}?utm_source=whats_new`}
            onClick={onViewChangelog}
            className="inline-block text-sm text-primary hover:underline"
          >
            View full release notes &rarr;
          </Link>
        </div>

        {/* Highlighted changes */}
        {release.highlights.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {release.highlights.map((change, i) => (
              <div key={i} className="rounded-lg border border-border bg-secondary/30 p-3 space-y-2">
                <p className="font-semibold text-sm">{change.title}</p>
                {change.description && (
                  <p className="text-xs text-muted-foreground">{change.description}</p>
                )}
                {change.image && (
                  <button
                    className="block cursor-zoom-in group"
                    onClick={() => onImageClick(change.image!, change.title)}
                  >
                    <img
                      src={change.image}
                      alt={change.title}
                      className="max-h-40 object-contain rounded-lg border border-transparent group-hover:border-primary/40 transition-colors"
                      loading="lazy"
                    />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export function WhatsNewDialog() {
  const { showDialog, closeDialog, unseenReleases } = useWhatsNew()
  const [currentSlide, setCurrentSlide] = useState(0)
  const [dontShowAgain, setDontShowAgain] = useState(false)
  const [lightboxImage, setLightboxImage] = useState<{ src: string; alt: string } | null>(null)

  const slideCount = unseenReleases.length
  const hasMultipleSlides = slideCount > 1

  // Reset slide index when dialog opens
  useEffect(() => {
    if (showDialog) {
      setCurrentSlide(0)
      setDontShowAgain(false)
    }
  }, [showDialog])

  const goPrev = useCallback(() => {
    setCurrentSlide((prev) => Math.max(0, prev - 1))
  }, [])

  const goNext = useCallback(() => {
    setCurrentSlide((prev) => Math.min(slideCount - 1, prev + 1))
  }, [slideCount])

  // Keyboard navigation
  useEffect(() => {
    if (!showDialog) return

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        goPrev()
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        goNext()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [showDialog, goPrev, goNext])

  function handleOpenChange(open: boolean) {
    if (!open && !lightboxImage) {
      handleClose()
    }
  }

  async function handleClose() {
    if (dontShowAgain) {
      try {
        await updateWhatsNewShowOnLogin(false)
      } catch {
        // Silently fail - non-critical
      }
    }
    closeDialog()
  }

  function handleViewChangelog(e: React.MouseEvent) {
    // Allow the Link to navigate, then close the dialog
    void e
    handleClose()
  }

  if (unseenReleases.length === 0) return null

  return (
    <>
    <Dialog.Root open={showDialog} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-2xl max-h-[calc(85svh-env(safe-area-inset-top)-env(safe-area-inset-bottom))] flex flex-col bg-card border border-border rounded-lg shadow-lg data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              <Dialog.Title className="text-lg font-semibold">
                What's New
              </Dialog.Title>
            </div>
            <Dialog.Close asChild>
              <button
                className="p-1.5 rounded-lg hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </Dialog.Close>
          </div>

          {/* Carousel Body */}
          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
            <div className="overflow-hidden">
              <div
                className="flex transition-transform duration-300 ease-in-out"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              >
                {unseenReleases.map((release) => (
                  <Slide
                    key={release.version}
                    release={release}
                    onViewChangelog={handleViewChangelog}
                    onImageClick={(src, alt) => setLightboxImage({ src, alt })}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] border-t border-border flex-shrink-0">
            {/* Don't show again checkbox */}
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
                className="rounded border-border"
              />
              <span className="text-xs text-muted-foreground">Don't show again</span>
            </label>

            {/* Dot indicators */}
            {hasMultipleSlides && (
              <div className="flex items-center gap-1.5">
                {unseenReleases.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentSlide(i)}
                    className={cn(
                      'w-2 h-2 rounded-full transition-colors',
                      i === currentSlide ? 'bg-primary' : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'
                    )}
                    aria-label={`Go to slide ${i + 1}`}
                  />
                ))}
              </div>
            )}

            {/* Navigation buttons */}
            {hasMultipleSlides ? (
              <div className="flex items-center gap-1">
                <button
                  onClick={goPrev}
                  disabled={currentSlide === 0}
                  className="p-1.5 rounded-lg hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label="Previous slide"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={goNext}
                  disabled={currentSlide === slideCount - 1}
                  className="p-1.5 rounded-lg hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label="Next slide"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div />
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>

      {lightboxImage && (
        <ImageLightbox
          src={lightboxImage.src}
          alt={lightboxImage.alt}
          open={true}
          onClose={() => setLightboxImage(null)}
        />
      )}
    </>
  )
}
